/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext } from "react";
import {
  ArrowUpRight,
  Download,
  MoreHorizontal,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  FileSpreadsheet,
  FileText,
  Calendar,
  Loader2,
  Menu,
  X,
  ChevronDown,
  Banknote,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import { AdminContext } from "../../../context/AdminContext";

const StatCard = ({
  title,
  count,
  increase,
  subtext,
  color = "bg-slate-900",
}) => (
  <div
    className={`${color} p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-sm border border-slate-800 relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300`}
  >
    <div className="flex justify-between items-start mb-2 md:mb-4">
      <span
        className={`text-xs md:text-sm font-medium ${
          color.includes("primary") || color.includes("CE257A")
            ? "text-white/90"
            : "text-gray-400"
        }`}
      >
        {title}
      </span>
      <div
        className={`p-1.5 md:p-2 rounded-full ${
          color.includes("primary") || color.includes("CE257A")
            ? "bg-white/20 text-white"
            : "bg-white/10 text-gray-400"
        }`}
      >
        <ArrowUpRight size={14} className="md:size-4" />
      </div>
    </div>
    <h3
      className={`text-2xl md:text-4xl font-bold mb-2 md:mb-4 ${
        color.includes("primary") || color.includes("CE257A")
          ? "text-white"
          : "text-white"
      }`}
    >
      {count}
    </h3>
    <div className="flex items-center gap-2">
      {increase && (
        <span
          className={`px-1.5 py-0.5 md:px-2 md:py-0.5 rounded text-[10px] md:text-xs font-medium ${
            color.includes("primary") || color.includes("CE257A")
              ? "bg-white/20 text-white"
              : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary"
          }`}
        >
          {increase}
        </span>
      )}
      <span
        className={`text-[10px] md:text-xs ${
          color.includes("primary") || color.includes("CE257A")
            ? "text-white/80"
            : "text-gray-400"
        }`}
      >
        {subtext}
      </span>
    </div>
  </div>
);

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return "NA";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to get random color based on name
const getAvatarColor = (name) => {
  if (!name) return "#6B7280";
  const colors = [
    "#EF4444",
    "#F59E0B",
    "#10B981",
    "#3B82F6",
    "#8B5CF6",
    "#EC4899",
    "#14B8A6",
    "#F97316",
    "#84CC16",
    "#6366F1",
  ];
  const index = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
};

// Helper function to get status icon and color
const getPaymentStatusInfo = (status) => {
  switch (status?.toLowerCase()) {
    case "success":
      return {
        icon: <CheckCircle size={12} className="md:size-3.5" />,
        color:
          "text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30",
        label: "Completed",
      };
    case "pending":
      return {
        icon: <Clock size={12} className="md:size-3.5" />,
        color:
          "text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30",
        label: "Pending",
      };
    case "failed":
      return {
        icon: <XCircle size={12} className="md:size-3.5" />,
        color: "text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30",
        label: "Failed",
      };
    case "cancelled":
      return {
        icon: <XCircle size={12} className="md:size-3.5" />,
        color:
          "text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30",
        label: "Cancelled",
      };
    default:
      return {
        icon: <AlertCircle size={12} className="md:size-3.5" />,
        color:
          "text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30",
        label: status || "Unknown",
      };
  }
};

const InvoiceDashboard = () => {
  const {
    fetchInvoiceDashboard,
    wagePayments,
    fetchWagePayments,
    exportInvoiceReport,
    loading,
  } = useContext(AdminContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentWagePayments, setRecentWagePayments] = useState([]);
  const [timeRange, setTimeRange] = useState("30");
  const [groupBy, setGroupBy] = useState("daily");
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportRange, setExportRange] = useState("30");
  const [exportType, setExportType] = useState("excel");
  const [exportLoading, setExportLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadWagePayments();
  }, [timeRange, groupBy]);

  // Load wage payments separately
  useEffect(() => {
    if (wagePayments && Array.isArray(wagePayments)) {
      processWagePayments(wagePayments);
    }
  }, [wagePayments]);

  const loadDashboardData = async () => {
    const data = await fetchInvoiceDashboard({
      range: parseInt(timeRange),
      groupBy,
    });
    if (data) {
      setDashboardData(data);
    }
  };

  const loadWagePayments = async () => {
    try {
      setLoadingPayments(true);
      // This will update the wagePayments state in context
      await fetchWagePayments();
    } catch (error) {
      console.error("Failed to load wage payments:", error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const processWagePayments = (payments) => {
    if (!payments || !Array.isArray(payments)) {
      setRecentWagePayments([]);
      return;
    }

    // Get the latest 4 wage payments
    const recentPayments = payments
      .sort(
        (a, b) =>
          new Date(b.paidAt || b.createdAt) -
          new Date(a.paidAt || a.createdAt),
      )
      .slice(0, 4)
      .map((payment) => {
        const statusInfo = getPaymentStatusInfo(payment.status);
        const platformFee = Number(
          payment.platformAmount ?? payment.platformFee ?? 0,
        );
        return {
          id: payment.id,
          user: {
            name: payment.caregiver?.name || "Unknown Caregiver",
            email: payment.caregiver?.email || "",
            initials: getInitials(payment.caregiver?.name),
            avatarColor: getAvatarColor(payment.caregiver?.name),
          },
          plan: `Contract #${payment.contract?.id?.substring(0, 8) || "N/A"}`,
          amount: platformFee,
          status: payment.status || "pending",
          date: payment.paidAt || payment.createdAt,
          displayDate: (payment.paidAt || payment.createdAt)
            ? new Date(payment.paidAt || payment.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "N/A",
          transactionId: (payment.id || "").substring(0, 8) || "N/A",
          paymentMethod: "Platform Fee",
          ...statusInfo,
        };
      });

    setRecentWagePayments(recentPayments);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ET", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      await exportInvoiceReport(exportType, parseInt(exportRange));
      setShowExportOptions(false);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  // Get current month-year for filename preview
  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportOptions && !event.target.closest(".export-dropdown")) {
        setShowExportOptions(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showExportOptions]);

  return (
    <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 bg-background dark:bg-dark-background min-h-screen p-3 md:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
        <div className="flex justify-between items-center md:block">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-text dark:text-dark-text">
              Invoice Dashboard
            </h1>
            <p className="text-sm md:text-base text-gray-500 dark:text-dark-muted mt-1">
              Track and analyze your payment revenues.
            </p>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 bg-card dark:bg-dark-surface rounded-lg"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Controls Container */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:flex flex-col md:flex-row gap-3 mt-3 md:mt-0`}
        >
          <div className="flex flex-col sm:flex-row gap-2 mb-3 md:mb-0">
            <div className="flex gap-2">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="flex-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-xl md:rounded-full font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last year</option>
              </select>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="flex-1 px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-xl md:rounded-full font-medium focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Export Button with Dropdown */}
          <div className="relative export-dropdown">
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              disabled={exportLoading}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl md:rounded-full font-medium transition-colors shadow-lg shadow-primary/20 dark:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Download size={18} />
                  <span className="hidden sm:inline">Export</span>
                  <ChevronDown
                    size={16}
                    className={`ml-1 transition-transform ${
                      showExportOptions ? "rotate-180" : ""
                    }`}
                  />
                </>
              )}
            </button>

            {showExportOptions && (
              <div className="absolute right-0 md:right-auto md:left-0 mt-2 w-full md:w-64 bg-card dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border p-4 z-50 animate-in fade-in zoom-in-95 duration-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-muted mb-2">
                      <Calendar size={14} className="inline mr-2" />
                      Export Date Range
                    </label>
                    <select
                      value={exportRange}
                      onChange={(e) => setExportRange(e.target.value)}
                      className="w-full px-3 py-2 text-sm md:text-base bg-accent dark:bg-gray-800 border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="90">Last 90 days</option>
                      <option value="365">Last year</option>
                      <option value="730">Last 2 years</option>
                      <option value="0">All time</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-muted mb-2">
                      Export Format
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setExportType("excel")}
                        className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                          exportType === "excel"
                            ? "bg-green-500 text-white"
                            : "bg-accent dark:bg-gray-800 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                      >
                        <FileSpreadsheet size={14} className="md:size-4" />
                        <span className="text-xs md:text-sm">Excel</span>
                      </button>
                      <button
                        onClick={() => setExportType("pdf")}
                        className={`flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                          exportType === "pdf"
                            ? "bg-red-500 text-white"
                            : "bg-accent dark:bg-gray-800 text-gray-600 dark:text-dark-muted hover:bg-gray-200 dark:hover:bg-gray-700"
                        }`}
                      >
                        <FileText size={14} className="md:size-4" />
                        <span className="text-xs md:text-sm">PDF</span>
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleExport}
                    disabled={exportLoading}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm md:text-base bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Download size={16} />
                        <span>
                          Export {exportType === "pdf" ? "PDF" : "Excel"}
                        </span>
                      </>
                    )}
                  </button>

                  <div className="p-3 bg-accent dark:bg-gray-800 rounded-lg">
                    <p className="text-xs text-gray-600 dark:text-dark-muted">
                      <strong>Export Details:</strong>
                    </p>
                    <ul className="text-xs text-gray-500 dark:text-dark-muted mt-1 space-y-1">
                      <li className="break-words">
                        • Date Range:{" "}
                        {exportRange === "0"
                          ? "All time"
                          : `Last ${exportRange} days`}
                      </li>
                      <li>
                        • Format: {exportType === "pdf" ? "PDF" : "Excel"}
                      </li>
                      {exportType === "excel" ? (
                        <li>• Direct download from server</li>
                      ) : (
                        <li>• Client-side generation with jsPDF</li>
                      )}
                      <li className="break-words">
                        • Filename: invoice-report-{getCurrentMonthYear()}.
                        {exportType}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          title="Total Revenue"
          count={
            dashboardData
              ? formatCurrency(dashboardData.stats?.totalPaidRevenue || 0)
              : "Loading..."
          }
          increase=""
          subtext="Total collected payments"
          color="bg-primary"
        />
        <StatCard
          title="Platform Fees Collected"
          count={
            dashboardData
              ? formatCurrency(dashboardData.stats?.totalPlatformFees || 0)
              : "Loading..."
          }
          increase=""
          subtext="20% from wage payments"
        />
        <StatCard
          title="Total Disbursed"
          count={
            dashboardData
              ? formatCurrency(dashboardData.stats?.totalDisbursed || 0)
              : "Loading..."
          }
          increase=""
          subtext="80% to caregivers"
        />
        <StatCard
          title="Recent Transactions"
          count={recentWagePayments.length}
          increase=""
          subtext={`Latest ${recentWagePayments.length} wage payments`}
        />
      </div>

      {/* Manual Payments Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard
          title="Total Manual Payments"
          count={dashboardData?.stats?.totalManualPayments || 0}
          increase=""
          subtext="All manual payment records"
          color="bg-slate-900"
        />
        <StatCard
          title="Pending Payments"
          count={dashboardData?.stats?.pendingManualPayments || 0}
          increase=""
          subtext="Awaiting payment"
          color="bg-slate-900"
        />
        <StatCard
          title="Completed Payments"
          count={dashboardData?.stats?.completedManualPayments || 0}
          increase=""
          subtext="Successfully paid"
          color="bg-slate-900"
        />
        <StatCard
          title="Total Collected"
          count={
            dashboardData
              ? formatCurrency(
                  dashboardData.stats?.totalManualPaymentCollected || 0,
                )
              : "Loading..."
          }
          increase=""
          subtext="From manual payments"
          color="bg-slate-900"
        />
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Revenue Analytics Chart */}
        <div className="lg:col-span-8 bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-sm border border-slate-800">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-6 gap-2">
            <h3 className="font-bold text-base md:text-lg text-white">
              Revenue Analytics (
              {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)})
            </h3>
            <div className="flex items-center gap-4">
              {loading && (
                <span className="text-xs md:text-sm text-gray-400 animate-pulse">
                  Loading...
                </span>
              )}
              <button className="text-gray-400 hover:text-gray-300">
                <MoreHorizontal size={18} />
              </button>
            </div>
          </div>
          <div className="h-60 md:h-80">
            {dashboardData?.chartData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dashboardData.chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={40}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tickFormatter={(value) => formatCurrency(value)}
                    width={60}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), "Amount"]}
                    labelFormatter={(label) => `Period: ${label}`}
                  />
                  <Bar
                    dataKey="platformFees"
                    name="Platform Fees"
                    fill="#CE257A"
                    radius={[4, 4, 0, 0]}
                    barSize={20}
                  />
                  <Bar
                    dataKey="disbursed"
                    name="Disbursed to Caregivers"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    barSize={20}
                  />
                  <Bar
                    dataKey="total"
                    name="Total Wage Payments"
                    fill="#10b981"
                    radius={[4, 4, 0, 0]}
                    barSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-sm md:text-base text-gray-400 text-center px-4">
                  {loading ? "Loading data..." : "No revenue data available"}
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 md:gap-4 mt-4 md:mt-6 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 md:w-3 md:h-3 bg-primary rounded-full"></div>
              <span className="text-xs text-gray-400">Platform Fees</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 md:w-3 md:h-3 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-400">Disbursed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 md:w-3 md:h-3 bg-emerald-500 rounded-full"></div>
              <span className="text-xs text-gray-400">Total</span>
            </div>
          </div>
        </div>

        {/* Revenue Breakdown */}
        <div className="lg:col-span-4 bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-sm border border-slate-800">
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <h3 className="font-bold text-base md:text-lg text-white">
              Revenue Breakdown
            </h3>
          </div>
          {dashboardData?.stats && (
            <div className="space-y-4 md:space-y-6">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs md:text-sm font-medium text-gray-400">
                    Platform Fees (20%)
                  </span>
                  <span className="text-xs md:text-sm font-bold text-white">
                    {formatCurrency(dashboardData.stats.totalPlatformFees || 0)}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1.5 md:h-2">
                  <div
                    className="bg-primary h-1.5 md:h-2 rounded-full"
                    style={{
                      width: `${
                        (dashboardData.stats.totalPlatformFees /
                          dashboardData.stats.totalPaidRevenue) *
                          100 || 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs md:text-sm font-medium text-gray-400">
                    Disbursed to Caregivers (80%)
                  </span>
                  <span className="text-xs md:text-sm font-bold text-white">
                    {formatCurrency(dashboardData.stats.totalDisbursed || 0)}
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1.5 md:h-2">
                  <div
                    className="bg-blue-500 h-1.5 md:h-2 rounded-full"
                    style={{
                      width: `${
                        (dashboardData.stats.totalDisbursed /
                          dashboardData.stats.totalPaidRevenue) *
                          100 || 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              <div className="pt-3 md:pt-4 border-t border-slate-700">
                <div className="flex justify-between items-center">
                  <span className="text-base md:text-lg font-bold text-white">
                    Total Wage Payments
                  </span>
                  <span className="text-base md:text-lg font-bold text-emerald-400">
                    {formatCurrency(
                      Number(dashboardData.stats.totalPaidRevenue) || 0,
                    )}
                  </span>
                </div>
              </div>

              <div className="bg-gray-800 p-3 md:p-4 rounded-xl">
                <h4 className="font-semibold text-xs md:text-sm text-white mb-2">
                  Insights
                </h4>
                <p className="text-xs text-gray-400">
                  Revenue tracked over the last {timeRange} days, grouped by{" "}
                  {groupBy} intervals. Platform retains 20% as service fee,
                  while 80% is disbursed to caregivers through monthly payouts.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Recent Wage Payments */}
        <div className="bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-sm border border-slate-800">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-6 gap-2">
            <h3 className="font-bold text-base md:text-lg text-white">
              Recent Wage Payments
            </h3>
            <div className="flex items-center gap-2">
              <Banknote size={14} className="md:size-4 text-gray-400" />
              <span className="text-xs text-gray-400">
                Latest 4 transactions
              </span>
            </div>
          </div>
          <div className="space-y-3 md:space-y-4">
            {loadingPayments ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3 md:gap-4">
                      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="h-3 w-24 md:h-4 md:w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                        <div className="h-2.5 w-36 md:h-3 md:w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                      </div>
                    </div>
                    <div className="h-5 w-16 md:h-6 md:w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : recentWagePayments.length > 0 ? (
              recentWagePayments.map((payment) => (
                <div
                  key={payment.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-2.5 md:p-3 hover:bg-gray-800 rounded-xl transition-colors gap-2 sm:gap-0"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-xs md:text-sm"
                      style={{ backgroundColor: payment.user.avatarColor }}
                    >
                      {payment.user.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="font-semibold text-sm text-white truncate">
                        {payment.user.name}
                      </h5>
                      <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-0.5">
                        <span className="text-xs text-gray-400 truncate">
                          {payment.plan}
                        </span>
                        <span className="text-xs text-gray-500 hidden md:inline">
                          •
                        </span>
                        <span className="text-xs text-gray-400">
                          {payment.displayDate}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        Transaction: {payment.transactionId}...
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:items-end gap-1 mt-2 sm:mt-0">
                    <span className="font-bold text-sm md:text-base text-white">
                      {formatCurrency(payment.amount)}
                    </span>
                    <span
                      className={`px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-bold flex items-center gap-1 ${payment.color}`}
                    >
                      {payment.icon}
                      <span className="hidden sm:inline">{payment.label}</span>
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 md:py-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <Banknote className="text-gray-400 md:size-6" size={20} />
                </div>
                <p className="text-sm text-gray-400">
                  No recent wage payments found
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Revenue Trends */}
        <div className="bg-slate-900 p-4 md:p-6 rounded-2xl md:rounded-3xl shadow-sm border border-slate-800">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2">
            <div>
              <h3 className="font-bold text-base md:text-lg text-white">
                Revenue Trends
              </h3>
              <p className="text-xs text-gray-400">
                Monthly revenue comparison
              </p>
            </div>
            <div className="px-2.5 py-1 bg-gray-800 rounded-lg text-xs font-medium text-gray-400 self-start">
              Range: Last {timeRange} days
            </div>
          </div>

          <div className="h-52 md:h-64 mt-3 md:mt-4">
            {dashboardData?.chartData?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dashboardData.chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#f1f5f9"
                    strokeOpacity={0.3}
                  />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={40}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    tickFormatter={(value) => formatCurrency(value)}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), "Amount"]}
                  />
                  <Line
                    type="monotone"
                    dataKey="platformFees"
                    name="Platform Fees"
                    stroke="#CE257A"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="disbursed"
                    name="Disbursed"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-sm md:text-base text-gray-400 text-center px-4">
                  {loading ? "Loading trends..." : "No trend data available"}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mt-3 md:mt-4 gap-3">
            <div className="flex gap-3 md:gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-primary rounded-sm"></div>
                <span className="text-[9px] md:text-[10px] text-gray-400">
                  Platform Fees
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-sm"></div>
                <span className="text-[9px] md:text-[10px] text-gray-400">
                  Disbursed
                </span>
              </div>
            </div>
            {dashboardData?.chartData?.length > 0 && (
              <div className="text-right">
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {dashboardData.chartData.length}
                </h2>
                <p className="text-[9px] md:text-[10px] text-gray-400">
                  Periods
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDashboard;
